import React, { useState, useEffect, useContext, useRef } from "react";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSelect,
    StandardModalState,
    TextInputWithIcon,
    UserSelectionChip,
    UserSelectionChipBody,
    UserSelectionChipIcon,
    UserSelectionChipText,
    UserSelectionControls,
    UserSelectionCount,
    UserSelectionFieldGroup,
    UserSelectionLabel,
    UserSelectionRemoveButton,
    UserSelectionSearchInput,
    UserSelectionSummaryBar,
    UserSelectionSummaryLabel,
    UserSelectionSwitch,
    UserSelectionToggleBox,
} from "src/components";
import SearchIcon from "src/img/new/search-gray.svg";
import { EnumUserGroup, FormType } from "../../components/Enums";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import axios from "axios";
import UserGroup from "src/img/new/users.svg";
import CloseIcon from "src/img/CloseButton@2x.png";
import PollsUsersList from "./PollsComponents/UsersList";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { USER_TYPE } from "src/constants/survey";
import { getVisibilityUsers } from "../visibilityApi";

const VisibilityModal = (props) => {
    const currentUser = useContext(UserContext);
    const controllerRef = useRef(new AbortController());

    const {
        togglePollVisibilityModal,
        showPollVisibilityModal,
        handleSave,
        pollParticipantUuids,
        clientsInPoll,
        employeesInPoll,
        employeesCountInPoll,
        clientsCountInPoll,
        modalTitle,
        formType,
        userGroupType
    } = props;
    const [selectedUserGroupType, setSelectedUserGroupType] = useState(EnumUserGroup.EMPLOYEES); // this identifies the values of the dropdown option, NOT the poll/survey usergroup type
    const [usersList, setUsersList] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [usersToDisplay, setUsersToDisplay] = useState([]);
    const [allEmployeesSelected, setAllEmployeesSelected] = useState(employeesInPoll || false);
    const [employeesCountSelected, setEmployeesCountSelected] = useState(employeesCountInPoll || 0);
    const [allClientsSelected, setAllClientsSelected] = useState(clientsInPoll || false);
    const [clientsCountSelected, setClientsCountSelected] = useState(clientsCountInPoll || 0);
    const [loadingFetchUsers, setLoadingFetchUsers] = useState(false);

    const [showEmployeesBadge, setShowEmployeesBadge] = useState(false);
    const [showClientsBadge, setShowClientsBadge] = useState(false);

    useEffect(() => {
        fetchUsersList(EnumUserGroup.EMPLOYEES);
    }, []);

    useEffect(() => {
        if (showPollVisibilityModal) {
            verifyUserGroupValues(formType, userGroupType);
        }
    }, [showPollVisibilityModal]);

    useEffect(() => {
        if (usersList) {
            if (pollParticipantUuids && selectedUsers.length === 0) {
                const preselectedUsers = usersList.filter(user => pollParticipantUuids.includes(user.uuid));
                setSelectedUsers(preselectedUsers);
            }
        }
    }, [usersList]);

    const fetchUsersList = async (selectedUserGroupType) => {
        if (controllerRef.current) {
            controllerRef.current.abort();
        }
        setLoadingFetchUsers(true);

        controllerRef.current = new AbortController();

        getVisibilityUsers(currentUser[UserKeys.TOKEN], formType, controllerRef.current.signal).then(users => {
            const requestingClients = selectedUserGroupType === EnumUserGroup.CLIENTS;
            const usersOfType = users.filter(user => user.isClient === requestingClients);
            setUsersList(usersOfType);
            setUsersToDisplay(usersOfType);
            setLoadingFetchUsers(false);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.error(error.getApiErrorDetails().message);
            }
            setLoadingFetchUsers(false);
        });
    };

    const retrieveUpdatedUsersList = async (fetchUserType) => {
        setSelectedUserGroupType(fetchUserType);
        fetchUsersList(fetchUserType);
    };

    const handleSearch = (e) => {
        const searchKey = e.target.value;
        if (searchKey.length) {
            const filteredUsers = usersList.filter(user =>
                user.firstName.toLowerCase().includes(searchKey.toLowerCase())
                || user.lastName.toLowerCase().includes(searchKey.toLowerCase())
            );
            setUsersToDisplay(filteredUsers);
        } else {
            setUsersToDisplay(usersList);
        }
    };

    const setAllSelectedByUserType = (isChecked, setAllSelectedByUser, setShowBadgeByUser) => {
        let selectedUsersTemp = [...selectedUsers];
        
        setAllSelectedByUser(isChecked);
        if (!isChecked) { // toggle is off
            const removeSelectedUsersFromList = selectedUsersTemp.filter((value) => 
                !usersList.some((user) => user.uuid === value.uuid));
            setSelectedUsers(removeSelectedUsersFromList);
            setShowBadgeByUser(false);
        } else {
            selectedUsersTemp = [...new Set([...selectedUsersTemp, ...usersList.map((i) => ({
                uuid: i.uuid,
                isClient: i.isClient
            }))])];
            setSelectedUsers(selectedUsersTemp);
            setShowBadgeByUser(true);
        }
    };

    const handleAllSwitch = (e) => {
        if (selectedUserGroupType === EnumUserGroup.CLIENTS) {
            setAllSelectedByUserType(e.target.checked, setAllClientsSelected, setShowClientsBadge);
        } else {
            setAllSelectedByUserType(e.target.checked, setAllEmployeesSelected, setShowEmployeesBadge);
        }
    };

    const removeSelectedPeopleItems = (userType) => {
        let selectedUsersTemp = [...selectedUsers];
        const isClient = userType === EnumUserGroup.CLIENTS;
        const removeSelectedUsersFromList = selectedUsersTemp.filter((value) => 
            value.isClient !== isClient
        );
        setSelectedUsers(removeSelectedUsersFromList);

        if (isClient) {
            setAllClientsSelected(false);
            setShowClientsBadge(false);
        } else {
            setAllEmployeesSelected(false);
            setShowEmployeesBadge(false);
        }
    };

    const handleSelectUser = (user) => {
        let tempUserSelectedList = [...selectedUsers];
        if (tempUserSelectedList.find((item) => item.uuid === user.uuid)) { // removes selected user from selected list
            tempUserSelectedList = tempUserSelectedList.filter((item) => item.uuid !== user.uuid);
            if (allEmployeesSelected || allClientsSelected) {
                if (selectedUserGroupType === EnumUserGroup.CLIENTS) {
                    setAllClientsSelected(false);
                } else {
                    setAllEmployeesSelected(false);
                }
            }
        } else { // adds selected user from selected list
            tempUserSelectedList.push({ uuid: user.uuid, isClient: user.isClient });
            if (!allEmployeesSelected || !allClientsSelected) {
                const tempTalliedUsersUuid = countUUIDsInUsers(usersList, tempUserSelectedList);
                if (selectedUserGroupType === EnumUserGroup.CLIENTS) {
                    if (usersList.length === tempTalliedUsersUuid) {
                        setAllClientsSelected(true);
                    }
                } else {
                    if (usersList.length === tempTalliedUsersUuid) {
                        setAllEmployeesSelected(true);
                    }
                }
            }
        }
        const talliedUsersUuid = countUUIDsInUsers(usersList, tempUserSelectedList);
        if (selectedUserGroupType === EnumUserGroup.CLIENTS) {
            setClientsCountSelected(talliedUsersUuid);
            setShowClientsBadge(talliedUsersUuid !== 0);
        } else {
            setEmployeesCountSelected(talliedUsersUuid);
            setShowEmployeesBadge(talliedUsersUuid !== 0);
        }

        setSelectedUsers(tempUserSelectedList);
    };

    const countUUIDsInUsers = (userList, selectedUsers) => {
        return userList.filter((userItem) => selectedUsers.some((selectedUser) => selectedUser.uuid === userItem.uuid)).length;
    };

    const verifyUserGroupValues = (formType, userGroupType) => {
        if (formType === FormType.SURVEYS) {
            if (userGroupType === EnumUserGroup.EMPLOYEES || userGroupType === EnumUserGroup.CLIENTS) {
                if (userGroupType === EnumUserGroup.EMPLOYEES) {
                    setShowEmployeesBadge(true);
                    setAllEmployeesSelected(true);
                }
                if (userGroupType === EnumUserGroup.CLIENTS) {
                    setShowClientsBadge(true);
                    setAllClientsSelected(true);
                }
            }
            if (userGroupType === EnumUserGroup.SELECTED_USERS) {
                if (employeesCountInPoll != 0) {
                    setShowEmployeesBadge(true);
                    setEmployeesCountSelected(employeesCountInPoll);
                }
                if (clientsCountInPoll != 0) {
                    setShowClientsBadge(true);
                    setClientsCountSelected(clientsCountInPoll);
                }
            }
            if (userGroupType === EnumUserGroup.ALL_USERS) {
                setShowEmployeesBadge(true);
                setAllEmployeesSelected(true);
                setShowClientsBadge(true);
                setAllClientsSelected(true);
            }
        }
        if (formType === FormType.POLLS) {
            if (userGroupType.allEmployees && userGroupType.allClients) {
                // identify if polls value for allEmployeesInPoll & allClientsInPoll is true
                const employeeValue = userGroupType.allEmployees.split(" ");
                if (employeeValue[0] === "All") {
                    setShowEmployeesBadge(true);
                    setAllEmployeesSelected(true);
                } else {
                    setShowEmployeesBadge(true);
                    setAllEmployeesSelected(false);
                    setEmployeesCountSelected((Number(employeeValue[0])));
                }
                const clientsValue = userGroupType.allClients.split(" ");
                if (clientsValue[0] === "All") {
                    setShowClientsBadge(true);
                    setAllClientsSelected(true);
                } else {
                    setShowClientsBadge(true);
                    setAllClientsSelected(false);
                    setClientsCountSelected((Number(clientsValue[0])));
                }
            }
            if (userGroupType.allEmployees && !userGroupType.allClients) {
                // identify if polls value for allEmployeesInPoll is true & allClientsInPoll is false
                const employeeValue = userGroupType.allEmployees.split(" ");
                if (employeeValue[0] === "All") {
                    setShowEmployeesBadge(true);
                    setAllEmployeesSelected(true);
                } else {
                    setShowEmployeesBadge(true);
                    setAllEmployeesSelected(false);
                    setEmployeesCountSelected((Number(employeeValue[0])));
                }
            }
            if (userGroupType.allClients && !userGroupType.allEmployees) {
                // identify if polls value for allEmployeesInPoll is false & allClientsInPoll is true
                const clientsValue = userGroupType.allClients.split();
                if (clientsValue[0] === "All") {
                    setShowClientsBadge(true);
                    setAllClientsSelected(true);
                } else {
                    setShowClientsBadge(true);
                    setAllClientsSelected(false);
                    setClientsCountSelected((Number(clientsValue[0])));
                }
            }
        }
    };

    const mapUsersToUuids = (selectedUsers) => {
        return selectedUsers.map(user => user.uuid);
    };

    const selectedUserGroupLabel = selectedUserGroupType === USER_TYPE.EMPLOYEES
        ? capitalizeString(pluralize(customNames.employees))
        : capitalizeString(pluralize(customNames.clients));
    const selectedUserGroupSearchLabel = selectedUserGroupType === USER_TYPE.EMPLOYEES
        ? pluralize(customNames.employees.toLowerCase())
        : pluralize(customNames.clients.toLowerCase());
    const selectedCount = countUUIDsInUsers(usersList, selectedUsers) ?? 0;

    return (
        <StandardModal
            open={showPollVisibilityModal}
            onClose={togglePollVisibilityModal}
            widthMode="table"
            title={modalTitle}
            description="Select all users or choose specific people who should be included."
            headerAside={(
                <div style={{ width: "13rem" }}>
                    <StandardModalSelect value={selectedUserGroupType} onChange={(e) => retrieveUpdatedUsersList(e.target.value)}>
                        <option value={EnumUserGroup.EMPLOYEES}>
                            {capitalizeString(pluralize(customNames.employees))}
                        </option>
                        <option value={EnumUserGroup.CLIENTS}>
                            {capitalizeString(pluralize(customNames.clients))}
                        </option>
                    </StandardModalSelect>
                </div>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={togglePollVisibilityModal}>
                        Cancel
                    </StandardModalActionButton>
                    <StandardModalActionButton
                        type="button"
                        $variant="primary"
                        onClick={() => handleSave(allEmployeesSelected, allClientsSelected, mapUsersToUuids(selectedUsers), employeesCountSelected, clientsCountSelected)}
                    >
                        Save
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionSummaryBar>
                    <UserSelectionSummaryLabel>Selected People:</UserSelectionSummaryLabel>
                    {showEmployeesBadge && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon>
                                    <img src={UserGroup} alt="" />
                                </UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {allEmployeesSelected ? "All" : employeesCountSelected} {capitalizeString(pluralize(customNames.employees))}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton type="button" onClick={() => removeSelectedPeopleItems(EnumUserGroup.EMPLOYEES)} aria-label="Remove selected employees">
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                    {showClientsBadge && (
                        <UserSelectionChip>
                            <UserSelectionChipBody>
                                <UserSelectionChipIcon>
                                    <img src={UserGroup} alt="" />
                                </UserSelectionChipIcon>
                                <UserSelectionChipText>
                                    {allClientsSelected ? "All" : clientsCountSelected} {capitalizeString(pluralize(customNames.clients))}
                                </UserSelectionChipText>
                            </UserSelectionChipBody>
                            <UserSelectionRemoveButton type="button" onClick={() => removeSelectedPeopleItems(EnumUserGroup.CLIENTS)} aria-label="Remove selected clients">
                                <img src={CloseIcon} alt="" />
                            </UserSelectionRemoveButton>
                        </UserSelectionChip>
                    )}
                </UserSelectionSummaryBar>
            </StandardModalSection>

            <StandardModalSection>
                <UserSelectionControls>
                    <UserSelectionFieldGroup>
                        <UserSelectionLabel>Select {selectedUserGroupLabel}</UserSelectionLabel>
                        <TextInputWithIcon
                            iconSrc={SearchIcon}
                            iconPosition="right"
                            iconOffset="0.875rem"
                            iconWidth="24px"
                            iconHeight="24px"
                        >
                            <UserSelectionSearchInput
                                placeholder={`Search ${selectedUserGroupSearchLabel}`}
                                onChange={handleSearch}
                                disabled={loadingFetchUsers}
                            />
                        </TextInputWithIcon>
                    </UserSelectionFieldGroup>
                    <UserSelectionFieldGroup>
                        <UserSelectionCount>
                            {loadingFetchUsers ? 0 : selectedCount} {selectedUserGroupLabel} Selected
                        </UserSelectionCount>
                        <UserSelectionToggleBox>
                            All {selectedUserGroupLabel}
                            <UserSelectionSwitch
                                onChange={handleAllSwitch}
                                checked={selectedUserGroupType === EnumUserGroup.CLIENTS ? allClientsSelected : allEmployeesSelected}
                                disabled={loadingFetchUsers}
                            />
                        </UserSelectionToggleBox>
                    </UserSelectionFieldGroup>
                </UserSelectionControls>

                {loadingFetchUsers ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <PollsUsersList
                        usersToDisplay={usersToDisplay}
                        selectedUserGroupType={selectedUserGroupType}
                        handleSelectUser={handleSelectUser}
                        selectedUsers={selectedUsers}
                    />
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default VisibilityModal;
